import { useCallback } from 'react';
import { createHISIDomainFlagKey, defaultInviteApiClient } from '../../clients';
import { useViralSettingsHaveISeenIt } from '../ViralSettings';
import { type InviteProcessingResponse, InviteProcessingResponseStatus } from '../../types';
import {
	type ViralSettingsDESEligibleStates,
	type UpdateDirectAccessSettingResponse,
} from '../ViralSettings/types';
import {
	getDirectAccessLocation,
	isAcceptableFailure,
	isAcceptableFailureForDirectAccess,
	resolveError,
} from '../../utils';
import { useAnalytics } from '../../analytics';
import {
	inviteFormSubmitted,
	inviteFormSubmittedTimings,
	domainsSettingsUpdated,
} from '../../analytics/ufoExperiences';
import { InvitePeopleAnalytics } from './InvitePeopleAnalytics';
import { useInvitePeopleContext } from './InvitePeopleContext';

export type DirectAccessSettingsHookProps = {
	selectedProducts: string[];
	viralSettingsDESEligibleStates: ViralSettingsDESEligibleStates;
	isViralSettingsEnabled: boolean;
};

const SKIPPED_RESPONSE: InviteProcessingResponse = {
	responseStatus: InviteProcessingResponseStatus.SKIPPED,
	flags: [],
};

const isAcceptable = (errorMessage: string, statusCode: number | undefined) => {
	return (
		isAcceptableFailureForDirectAccess(errorMessage, statusCode) ||
		isAcceptableFailure(errorMessage, statusCode)
	);
};

const useDirectAccessSettings = ({
	selectedProducts,
	viralSettingsDESEligibleStates,
	isViralSettingsEnabled,
}: DirectAccessSettingsHookProps) => {
	const { productId, ...invitePeopleData } = useInvitePeopleContext();
	const cloudId = invitePeopleData.cloudId ?? '';
	const analytics = useAnalytics(InvitePeopleAnalytics);
	const { setViralSettingsHaveISeenIt } = useViralSettingsHaveISeenIt();
	const processDirectAccessSettings = useCallback(async (): Promise<
		InviteProcessingResponse<UpdateDirectAccessSettingResponse, unknown>
	> => {
		// Adding empty flag array in preparation for flags to be refactored from getViralSettingsFlags in near future
		if (!isViralSettingsEnabled || selectedProducts.length !== 1 || !cloudId) {
			return SKIPPED_RESPONSE;
		}

		const product = selectedProducts[0];
		const { checked: domains, unchecked: uncheckedDomains } = viralSettingsDESEligibleStates;

		uncheckedDomains.forEach((domain) => {
			const domainKey = createHISIDomainFlagKey(product, cloudId, domain);
			setViralSettingsHaveISeenIt(domainKey, product, domain);
		});
		// Only add request if there are domains to update
		if (domains.length === 0) {
			return SKIPPED_RESPONSE;
		}
		const location = getDirectAccessLocation(productId);

		try {
			domainsSettingsUpdated.start();
			inviteFormSubmitted.mark(inviteFormSubmittedTimings.updateDirectAccessSettings.startMark);

			const response = await defaultInviteApiClient.updateDirectAccessSettings({
				cloudId,
				product,
				domains,
				location,
			});

			analytics.approvedDomainsSettingsSucceededSLO({
				addedDomains: domains,
			});
			domainsSettingsUpdated.success();

			return {
				responseStatus: InviteProcessingResponseStatus.SUCCESS,
				response: response,
				flags: [],
			} as InviteProcessingResponse;
		} catch (err: unknown) {
			const { responseCode, errorMessage, error } = resolveError(err);

			analytics.approvedDomainsSettingsFailed({
				proposedDomains: domains,
				responseCode,
				errorMessage,
			});

			const isSuccess = isAcceptable(errorMessage, responseCode);

			if (isSuccess) {
				// no domains were added so we will send an empty value for the addedDomains attribute
				analytics.approvedDomainsSettingsSucceededSLO({ addedDomains: [] });
				domainsSettingsUpdated.success();
			} else {
				analytics.approvedDomainsSettingsFailedSLO({
					proposedDomains: domains,
					responseCode,
					errorMessage,
				});
				domainsSettingsUpdated.failure();
			}

			return {
				responseStatus: InviteProcessingResponseStatus.ERROR,
				error,
				flags: [],
			};
		} finally {
			inviteFormSubmitted.mark(inviteFormSubmittedTimings.updateDirectAccessSettings.endMark);
		}
	}, [
		analytics,
		cloudId,
		isViralSettingsEnabled,
		productId,
		selectedProducts,
		setViralSettingsHaveISeenIt,
		viralSettingsDESEligibleStates,
	]);

	return {
		processDirectAccessSettings,
	};
};

export default useDirectAccessSettings;
